import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import useApi from "@/hooks/useApi";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  Divider
} from "@/components/ds";
import {
  Lightbulb,
  CheckCircle,
  FileText,
  ShoppingCart,
  Package,
  Tag,
  Bot
} from "@/components/ds/icons";
import LoadingSpinner from "@/components/LoadingSpinner";
import BigCommerceAccounts from "./BigCommerceAccounts";
import BigCommerceTestConnection from "./BigCommerceTestConnection";
import BigCommerceBotKnowledge from "./BigCommerceBotKnowledge";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationDataMapping from "../shared/IntegrationDataMapping";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel, { type SkillDef } from "../SkillControlsPanel";
import ShopperProfileTab from "../shared/ShopperProfileTab";
import useAdminFeatures from "@/hooks/useAdminFeatures";

const BC_SCOPE_OPTIONS = ["Customer", "Order", "Product"];

interface BigCommerceConfig {
  id: number;
  siteId: number;
  displayName: string;
  storeHash: string;
  storeName: string;
  storeUrl: string;
  enabled: boolean;
  isActive?: boolean;
  disabledAt?: string | null;
  disabledBy?: string | null;
  createdAt: string;
  accessTokenMasked: string;
  widgetDeployed?: boolean;
  scriptTagUuid?: string | null;
}

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES: {
  title: string;
  Icon: React.ComponentType<{ className?: string }>;
  items: string[];
}[] = [
  {
    title: "Customer lookup",
    Icon: ShoppingCart,
    items: [
      "Find any customer instantly by email address — name, account balance, total order count, and group membership",
      "Retrieve full customer details including billing and shipping addresses, stored payment methods, and pricing tier",
      "Works in real time during the chat so agents can answer 'what's my account balance?' or 'what group am I in?' instantly",
      "bigcommerce_get_customer — AI looks up customers automatically during chat; no manual CRM lookup needed"
    ]
  },
  {
    title: "Order management",
    Icon: Package,
    items: [
      "Look up any order by ID — returns line items, quantities, pricing, shipping address, carrier, and tracking number",
      "Find all recent orders for a customer's email — returns order IDs, statuses, and totals",
      "AI can answer 'where is my order?' with live data directly from BigCommerce — no agent needed",
      "Order statuses supported: Pending, Awaiting Payment, Awaiting Fulfillment, Shipped, Completed, Refunded, Cancelled"
    ]
  },
  {
    title: "Product search",
    Icon: Tag,
    items: [
      "Search your BigCommerce product catalog by keyword — returns name, SKU, price, inventory level, and description",
      "AI can answer pre-sales questions about availability, variants, and pricing from live catalog data",
      "Sale price returned when active — AI can highlight savings in conversation",
      "Product availability status: available / disabled / preorder — AI communicates this naturally",
      "Compare any two products side by side — price, variants, stock, and group pricing"
    ]
  },
  {
    title: "Cart & abandoned cart recovery",
    Icon: ShoppingCart,
    items: [
      "bigcommerce_create_cart: AI builds a cart with selected products and returns a direct checkout link — zero friction for customers",
      "bigcommerce_get_abandoned_carts: Find carts abandoned mid-checkout by email — powers cart recovery workflows",
      "bigcommerce_apply_coupon: AI applies a coupon code to the visitor's cart during the conversation to close hesitant buyers",
      "Abandoned cart recovery: Starter Wizard template combines cart lookup + coupon offer to re-engage lost sales"
    ]
  },
  {
    title: "AI workflow tools",
    Icon: Bot,
    items: [
      "33 tools available: get_customer, customer_insights, get_order, search_orders, get_orders, search_products, create_cart, get_abandoned_carts, apply_coupon, get_cart, add_order_note, check_stock, get_tracking, get_reviews, get_variants, check_pricing, get_brand_products, cancel_order, get_refunds, get_invoices, update_cart, get_wishlist, get_shipping_methods, get_store_config, get_categories, compare_products, get_recommendations, create_discount, create_refund, get_custom_fields, get_gift_certificates, get_price_lists, create_quote",
      "No workflow configuration required — the AI decides when to call BigCommerce based on conversation context",
      "Multiple BigCommerce stores supported — connect different storefronts or brands on the same Velaro site",
      "Field mapping is optional — the AI works immediately without it; mapping adds agent sidebar and HTTP POST variables"
    ]
  }
];

// ─── AI Skill Package Definitions ─────────────────────────────────────────────
// These map to SkillPackageDefinitions.cs on the server and subscription flags.
// Each package gates a group of skills independently.

interface SkillPackageDef {
  id: string;
  name: string;
  description: string;
  subscriptionFlag: string;
  listPrice: number;
  discountedPrice: number;
  skills: SkillDef[];
}

const SKILL_PACKAGES: SkillPackageDef[] = [
  {
    id: "bigcommerce-core",
    name: "Core",
    description:
      "Essential store skills — product search, orders, tracking, inventory",
    subscriptionFlag: "enableBigCommerceCore",
    listPrice: 500,
    discountedPrice: 250,
    skills: [
      {
        name: "bigcommerce_search_products",
        label: "Search products",
        description:
          "Searches the product catalog by keyword. Returns name, SKU, price, inventory level, and description."
      },
      {
        name: "bigcommerce_check_stock",
        label: "Check stock level",
        description:
          "Checks real-time inventory for a product by name. Returns quantity on hand and urgency messaging when stock is low."
      },
      {
        name: "bigcommerce_get_variants",
        label: "Get product variants",
        description:
          "Returns all variants for a product \u2014 colors, sizes, styles \u2014 with per-variant SKU, price, and stock level."
      },
      {
        name: "bigcommerce_get_order",
        label: "Get order by ID",
        description:
          "Retrieves a specific order with status, line items, shipping address, and tracking information."
      },
      {
        name: "bigcommerce_search_orders",
        label: "Search orders by email",
        description:
          "Looks up recent orders for a customer email. Returns order IDs, statuses, dates, and totals."
      },
      {
        name: "bigcommerce_get_tracking",
        label: "Get shipment tracking",
        description:
          "Returns carrier, tracking number, and a direct tracking URL for a BigCommerce order."
      },
      {
        name: "bigcommerce_get_customer",
        label: "Get customer details",
        description:
          "Fetches full customer record including billing/shipping addresses, payment methods, and group membership."
      },
      {
        name: "bigcommerce_get_categories",
        label: "Browse categories",
        description:
          "Returns the product category tree. Helps customers navigate to the right product group."
      },
      {
        name: "bigcommerce_get_reviews",
        label: "Get product reviews",
        description:
          "Fetches approved customer reviews. Returns avg star rating, total count, and top review highlights."
      },
      {
        name: "bigcommerce_get_store_config",
        label: "Store config",
        description:
          "Gets store settings \u2014 currency, locale, contact info. Helps the bot format prices correctly."
      }
    ]
  },
  {
    id: "bigcommerce-commerce",
    name: "Commerce",
    description:
      "Cart recovery, checkout, recommendations, customer intelligence",
    subscriptionFlag: "enableBigCommerceCommerce",
    listPrice: 1000,
    discountedPrice: 500,
    skills: [
      {
        name: "bigcommerce_create_cart",
        label: "Create cart / checkout link",
        description:
          "Builds a BigCommerce cart with selected products and returns a direct checkout link for the customer."
      },
      {
        name: "bigcommerce_get_abandoned_carts",
        label: "Find abandoned carts",
        description:
          "Finds carts abandoned mid-checkout by customer email \u2014 powers cart recovery workflows."
      },
      {
        name: "bigcommerce_apply_coupon",
        label: "Apply coupon code",
        description:
          "Applies a discount coupon to the visitor's BigCommerce cart during the conversation."
      },
      {
        name: "bigcommerce_get_cart",
        label: "View active cart",
        description:
          "Gets the active cart for a customer by email \u2014 item count, total, and product names."
      },
      {
        name: "bigcommerce_update_cart",
        label: "Update cart",
        description:
          "Changes item quantities or removes items from a cart. Set quantity to 0 to remove an item."
      },
      {
        name: "bigcommerce_get_recommendations",
        label: "Product recommendations",
        description:
          "Returns up to 5 products from the same category as a source product for upselling."
      },
      {
        name: "bigcommerce_compare_products",
        label: "Compare products",
        description:
          "Compare two BigCommerce products side by side \u2014 prices, stock, variants, and key differences."
      },
      {
        name: "bigcommerce_customer_insights",
        label: "Customer intelligence",
        description:
          "Calculates lifetime value, VIP status, purchase frequency, and top products for any customer."
      },
      {
        name: "bigcommerce_get_brand_products",
        label: "Browse by brand",
        description:
          "Returns products filtered by brand name for catalog discovery queries."
      },
      {
        name: "bigcommerce_get_wishlist",
        label: "Get wishlist",
        description:
          "Views items saved to a customer's wishlist. Useful for re-engagement."
      }
    ]
  },
  {
    id: "bigcommerce-b2b-ops",
    name: "B2B & Operations",
    description: "Wholesale pricing, quotes, cancellations, refunds, invoicing",
    subscriptionFlag: "enableBigCommerceBtoBOps",
    listPrice: 1000,
    discountedPrice: 500,
    skills: [
      {
        name: "bigcommerce_check_pricing",
        label: "Check customer pricing",
        description:
          "Looks up customer-group-specific pricing. Returns the price that specific B2B customer would pay."
      },
      {
        name: "bigcommerce_get_price_lists",
        label: "Get price lists",
        description:
          "Look up BigCommerce price lists and find which one applies to a customer by email."
      },
      {
        name: "bigcommerce_create_quote",
        label: "Create B2B quote",
        description:
          "Create a BigCommerce quote (draft order) for B2B customers. Returns quote number, total, and expiry date."
      },
      {
        name: "bigcommerce_get_custom_fields",
        label: "Get product custom fields",
        description:
          "Get custom fields for a product \u2014 dimensions, materials, certifications, and care instructions."
      },
      {
        name: "bigcommerce_cancel_order",
        label: "Cancel order",
        description:
          "Cancels a pending or processing order by setting its status to Cancelled. Only works on unshipped orders."
      },
      {
        name: "bigcommerce_get_refunds",
        label: "Get refunds",
        description:
          "Gets refund/return details for an order \u2014 refund amounts, reasons, and line items affected."
      },
      {
        name: "bigcommerce_get_invoices",
        label: "Get invoices",
        description:
          "Retrieves invoice data for an order \u2014 invoice number, amounts, and billing details."
      },
      {
        name: "bigcommerce_add_order_note",
        label: "Add order note",
        description:
          "Adds an internal staff note to a BigCommerce order \u2014 visible in admin only, not to the customer."
      }
    ]
  },
  {
    id: "bigcommerce-logistics",
    name: "Logistics",
    description: "Shipping rates, gift certificates, order history, promotions",
    subscriptionFlag: "enableBigCommerceLogistics",
    listPrice: 500,
    discountedPrice: 250,
    skills: [
      {
        name: "bigcommerce_get_shipping_methods",
        label: "Shipping methods",
        description:
          "Gets available shipping options and rates. Helps customers choose shipping during checkout."
      },
      {
        name: "bigcommerce_get_gift_certificates",
        label: "Look up gift certificate",
        description:
          "Look up a gift certificate by code or email. Returns balance, status, and expiry date."
      },
      {
        name: "bigcommerce_get_orders",
        label: "Get customer orders",
        description:
          "Retrieve recent orders for a BigCommerce customer by their internal customer ID."
      },
      {
        name: "bigcommerce_create_discount",
        label: "Create discount code",
        description:
          "Creates a real-time BigCommerce promotion + coupon code with configurable usage limit and expiry."
      },
      {
        name: "bigcommerce_create_refund",
        label: "Create refund",
        description:
          "Issues a manual refund on a BigCommerce order. Supports partial refunds by amount."
      }
    ]
  }
];

// Flatten all skills from packages for the SkillControlsPanel
const AI_TOOLS = SKILL_PACKAGES.flatMap((pkg) =>
  pkg.skills.map((s) => ({ ...s, group: pkg.name }))
);

const COMING_SOON_TOOLS: {
  name: string;
  label: string;
  description: string;
}[] = [];

// ─── How it all works ─────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <SectionCard
      title="How BigCommerce works in Velaro"
      description="Three steps — connect once, everything else is automatic."
      className="mb-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Step 1 */}
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-zinc-800 text-white text-xs font-bold flex items-center justify-center shrink-0">
              1
            </span>
            <span className="font-semibold text-zinc-700 text-sm">
              Connect your store
            </span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Paste two values from your BigCommerce dashboard and you're live.
          </p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">
                Store Hash
              </span>
              <span className="text-zinc-400">from Store Settings → API</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">
                Access Token
              </span>
              <span className="text-zinc-400">from API Account</span>
            </div>
          </div>
          {/* Arrow */}
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        {/* Step 2 */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              2
            </span>
            <span className="font-semibold text-green-800 text-sm">
              AI skills go live
            </span>
          </div>
          <p className="text-xs text-green-700 mb-3">
            29 tools activate immediately — zero configuration. The AI calls
            them based on what visitors ask.
          </p>
          <div className="grid grid-cols-2 gap-1">
            {[
              "Customer lookup",
              "Order status",
              "Product search",
              "Cart recovery",
              "Coupon apply",
              "Tracking link",
              "Stock check",
              "Reviews",
              "Variants",
              "Brand browse",
              "Pricing",
              "Checkout link",
              "Cart view",
              "Order notes",
              "Abandoned carts",
              "Customer pricing"
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-1 text-[10px] text-green-700"
              >
                <svg
                  viewBox="0 0 8 8"
                  width="8"
                  height="8"
                  fill="none"
                  className="shrink-0"
                >
                  <path
                    d="M1 4L3 6L7 2"
                    stroke="#16a34a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {t}
              </div>
            ))}
          </div>
          {/* Arrow */}
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        {/* Step 3 */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
              3
            </span>
            <span className="font-semibold text-blue-800 text-sm">
              Data surfaces everywhere
            </span>
          </div>
          <p className="text-xs text-blue-700 mb-3">
            Data appears in 3 places automatically — all without any
            configuration.
          </p>
          <div className="space-y-2">
            <div className="bg-blue-50/60 p-2 ring-1 ring-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                Agent sidebar
              </div>
              <div className="text-[10px] text-blue-600 mt-0.5">
                BigCommerce tab → Customer / Orders / Products panels with full
                data
              </div>
            </div>
            <div className="bg-blue-50/60 p-2 ring-1 ring-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                Contact tab strip
              </div>
              <div className="text-[10px] text-blue-600 mt-0.5">
                Group, Lifetime Value, and Order Count shown at a glance on the
                main info panel
              </div>
            </div>
            <div className="bg-blue-50/60 p-2 ring-1 ring-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                HTTP POST variables
              </div>
              <div className="text-[10px] text-blue-600 mt-0.5">
                Field Reference tab → copy variable names to push data to your
                CRM or ticketing system
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Capabilities panel ───────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <SectionCard className="mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <>
          <Divider />
          <div className="px-6 pb-6">
            <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
              <strong>AI tools work without any configuration.</strong> Connect
              your BigCommerce store (Store Hash + Access Token) and the AI can
              immediately call customer, order, and product lookup tools during
              any conversation. No JSON, no field mapping, no webhook setup
              required — the AI orchestrates tool calls automatically.
            </div>

            <div className="space-y-2">
              {CAPABILITIES.map((cap, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-zinc-100 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleSection(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                    aria-expanded={openSections.has(i)}
                  >
                    <div className="flex items-center gap-3">
                      <cap.Icon className="w-4 h-4 text-zinc-500" />
                      <span className="text-sm font-medium text-zinc-700">
                        {cap.title}
                      </span>
                    </div>
                    <svg
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                    </svg>
                  </button>
                  {openSections.has(i) && (
                    <div className="px-4 pb-3 space-y-1.5 border-t border-zinc-50">
                      {cap.items.map((item, j) => (
                        <div key={j} className="flex items-start gap-2 pt-1.5">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-green-500" />
                          <span className="text-xs text-zinc-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-4 text-xs text-blue-800">
              <div className="font-semibold mb-1">How orchestration works</div>
              <p className="mb-2">
                The BigCommerce AI node automatically queries your store as part
                of AI conversations. Use the{" "}
                <strong>E-commerce Specialist (Orchestration)</strong> template
                to create a dedicated BigCommerce AI that a main routing AI can
                hand off to when it detects order or product intent.
              </p>
              <p>
                Field mapping and HTTP POST nodes are optional extras — they're
                useful for pushing data to your CRM, ticketing system, or email
                platform after the AI resolves the conversation. The AI
                orchestration layer handles the conversation itself without any
                extra config.
              </p>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ─── Data Mapping ─────────────────────────────────────────────────────────────

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-zinc-200 text-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

const BC_FIELD_REFERENCE = [
  {
    scope: "Customer",
    scopeKey: "customer",
    fields: [
      {
        field: "id",
        label: "Customer ID",
        search: true,
        show: true,
        notes: "BigCommerce internal customer ID"
      },
      {
        field: "email",
        label: "Email",
        search: true,
        show: true,
        notes: "Primary identifier for lookup"
      },
      {
        field: "first_name",
        label: "First Name",
        search: true,
        show: true,
        notes: ""
      },
      {
        field: "last_name",
        label: "Last Name",
        search: true,
        show: true,
        notes: ""
      },
      {
        field: "company",
        label: "Company",
        search: true,
        show: true,
        notes: "Business account name"
      },
      { field: "phone", label: "Phone", search: false, show: true, notes: "" },
      {
        field: "orders_count",
        label: "Total Orders",
        search: false,
        show: true,
        notes: "Lifetime order count"
      },
      {
        field: "total_spent",
        label: "Lifetime Value",
        search: false,
        show: true,
        notes: "Total revenue from customer"
      },
      {
        field: "customer_group_id",
        label: "Price Group",
        search: false,
        show: true,
        notes: "Group ID for pricing tier"
      },
      {
        field: "notes",
        label: "Customer Notes",
        search: false,
        show: true,
        notes: "Internal admin notes"
      },
      {
        field: "accepts_product_review_abandoned_cart_emails",
        label: "Marketing Consent",
        search: false,
        show: false,
        notes: "Email marketing opt-in status"
      }
    ]
  },
  {
    scope: "Order",
    scopeKey: "order",
    fields: [
      {
        field: "id",
        label: "Order ID",
        search: true,
        show: true,
        notes: "Numeric order ID (e.g. 12345)"
      },
      {
        field: "status",
        label: "Status",
        search: false,
        show: true,
        notes: "Pending / Shipped / Completed / Refunded"
      },
      {
        field: "date_created",
        label: "Order Date",
        search: false,
        show: true,
        notes: "ISO date string"
      },
      {
        field: "total_inc_tax",
        label: "Order Total",
        search: false,
        show: true,
        notes: "Total including tax"
      },
      {
        field: "items_total",
        label: "Item Count",
        search: false,
        show: true,
        notes: "Total number of line items"
      },
      {
        field: "billing_address.email",
        label: "Billing Email",
        search: true,
        show: true,
        notes: "Nested billing object"
      },
      {
        field: "billing_address.first_name",
        label: "Billing First Name",
        search: false,
        show: true,
        notes: ""
      },
      {
        field: "shipping_addresses",
        label: "Shipping Address",
        search: false,
        show: true,
        notes: "Array — use first entry"
      },
      {
        field: "payment_method",
        label: "Payment Method",
        search: false,
        show: false,
        notes: ""
      },
      {
        field: "coupon_discount",
        label: "Discount Applied",
        search: false,
        show: false,
        notes: "Total coupon value"
      }
    ]
  },
  {
    scope: "Product",
    scopeKey: "product",
    fields: [
      {
        field: "id",
        label: "Product ID",
        search: false,
        show: true,
        notes: "BigCommerce product ID"
      },
      {
        field: "name",
        label: "Product Name",
        search: true,
        show: true,
        notes: "Full product title"
      },
      {
        field: "sku",
        label: "SKU",
        search: true,
        show: true,
        notes: "Stock keeping unit"
      },
      {
        field: "price",
        label: "Price",
        search: false,
        show: true,
        notes: "Base price excluding tax"
      },
      {
        field: "sale_price",
        label: "Sale Price",
        search: false,
        show: true,
        notes: "Discounted price if active"
      },
      {
        field: "inventory_level",
        label: "Stock Level",
        search: false,
        show: true,
        notes: "Units available (0 = out of stock)"
      },
      {
        field: "availability",
        label: "Availability",
        search: false,
        show: true,
        notes: "available / disabled / preorder"
      },
      {
        field: "description",
        label: "Description",
        search: false,
        show: false,
        notes: "Full HTML description"
      },
      {
        field: "custom_url.url",
        label: "Product URL",
        search: false,
        show: true,
        notes: "Storefront product page path"
      }
    ]
  }
];

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* graceful */
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy variable: {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${
        copied
          ? "border-green-300 bg-green-50 text-green-700"
          : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
      }`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "✓" : `{${variable}}`}
    </button>
  );
}

function BigCommerceFieldReferenceGuide() {
  const [activeScope, setActiveScope] = useState("Customer");
  const scopeData =
    BC_FIELD_REFERENCE.find((s) => s.scope === activeScope) ??
    BC_FIELD_REFERENCE[0];

  return (
    <div>
      {/* What this tab is */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4">
        <div className="font-semibold text-blue-900 text-sm mb-1">
          What this is
        </div>
        <p className="text-xs text-blue-800 leading-relaxed">
          A reference of every standard BigCommerce field name and the variable
          syntax you can use in workflow HTTP POST nodes to push data to other
          systems (e.g. your CRM, ticketing, or email platform).
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 text-xs">
          <div className="flex items-start gap-2 text-blue-800">
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
            <span>
              <strong>Agent sidebar</strong> — automatically shows customer,
              order, and product data during every live chat. No configuration
              needed. The sidebar always displays the same standard BigCommerce
              fields for every store.
            </span>
          </div>
          <div className="flex items-start gap-2 text-blue-800">
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
            <span>
              <strong>At-a-glance strip</strong> — the main Contact tab shows
              customer group, LTV, and order count at a glance so agents don't
              have to click to the BigCommerce tab for a quick reference.
            </span>
          </div>
          <div className="flex items-start gap-2 text-blue-800">
            <FileText className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
            <span>
              <strong>HTTP POST variables</strong> — copy any variable below and
              paste it into a workflow HTTP POST node body to forward
              BigCommerce data to another system after the conversation
              resolves.
            </span>
          </div>
        </div>
      </div>

      {/* Scope selector */}
      <div className="flex gap-2 mb-4">
        {BC_FIELD_REFERENCE.map((s) => (
          <button
            key={s.scope}
            type="button"
            onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeScope === s.scope
                ? "bg-zinc-800 text-white"
                : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
          >
            {s.scope}
          </button>
        ))}
      </div>

      {/* Workflow variable usage example */}
      <div className="bg-zinc-50 ring-1 ring-zinc-200 p-3 mb-4 text-xs text-zinc-600">
        <div className="flex items-center justify-between mb-1.5">
          <strong className="text-zinc-700">
            Using these in a workflow HTTP POST node
          </strong>
          <span className="text-zinc-400">
            Click any variable in the table to copy
          </span>
        </div>
        <CodeBlock>{`// Paste into HTTP POST node body → sends BigCommerce data to your CRM/ticketing system
{
  "customer_email":  "{bigcommerce.customer.email}",
  "customer_name":   "{bigcommerce.customer.first_name} {bigcommerce.customer.last_name}",
  "account_tier":    "{bigcommerce.customer.customer_group_id}",
  "lifetime_value":  "{bigcommerce.customer.total_spent}",
  "order_id":        "{bigcommerce.order.id}",
  "order_status":    "{bigcommerce.order.status}",
  "order_total":     "{bigcommerce.order.total_inc_tax}"
}`}</CodeBlock>
      </div>

      {/* Field reference table — no fake checkboxes */}
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-zinc-800 text-white text-xs font-semibold flex items-center gap-2">
          <span>{scopeData.scope} fields</span>
          <span className="text-zinc-400 font-normal">
            — all of these are available in every connected store
          </span>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  BigCommerce field
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Friendly name
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Variable to copy
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                >
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                    {f.field}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <CopyVariableButton
                      variable={`bigcommerce.${scopeData.scopeKey}.${f.field}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 italic">
                    {f.notes || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-zinc-400 mt-3">
        These are the standard BigCommerce v2/v3 API field names. They are
        identical across every BigCommerce store. The AI, agent sidebar, and
        at-a-glance strip all use these fields automatically — no selection or
        mapping needed.
      </p>
    </div>
  );
}

// ─── Deploy Widget Section ─────────────────────────────────────────────────────

function DeployWidgetCallout({ widgetDeployed }: { widgetDeployed: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-blue-800 mb-0.5">
            {widgetDeployed
              ? "✓ Widget deployed to this store"
              : "Deploy the chat widget to your store"}
          </div>
          <div className="text-xs text-blue-600">
            Configure your chat window styling, then grab the embed code from
            Deployments to add it to your store.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SecondaryButton onClick={() => navigate("/chat_window")}>
            Chat window
          </SecondaryButton>
          <PrimaryButton onClick={() => navigate("/deployment")}>
            Get embed code
          </PrimaryButton>
        </div>
      </div>
      <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
        <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <strong>Tip:</strong> You can also create a{" "}
          <strong>Knowledge Base Q&A</strong> page widget from{" "}
          <Link to="/deployment" className="underline hover:text-blue-900">
            Deployments → Page Widgets
          </Link>{" "}
          for a lightweight, AI-only Q&A experience — no live agent needed.
          Great as a standalone product.
        </span>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function BigCommerce() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [configs, setConfigs] = useState<BigCommerceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const { enableEcommerceSync } = useAdminFeatures();
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "config"
    | "bot-knowledge"
    | "skills"
    | "mapping"
    | "shoppers"
    | "log"
  >("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [bcInstallBanner, setBcInstallBanner] = useState<
    "claiming" | "success" | "error" | null
  >(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("BigCommerce/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("BigCommerce config fetch error", ex);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Handle ?bcInstall={uuid} — merchant redirected here after BC App Marketplace install
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uuid = params.get("bcInstall");
    const bcError = params.get("bcError");
    if (bcError) {
      setBcInstallBanner("error");
      navigate("/integrations/bigcommerce", { replace: true });
      return;
    }
    if (!uuid) return;
    navigate("/integrations/bigcommerce", { replace: true });
    setBcInstallBanner("claiming");
    api.admin
      .post("BigCommerce/Marketplace/ClaimInstall", { uuid })
      .then(async (resp) => {
        if (resp.ok) {
          setBcInstallBanner("success");
          fetchConfigs();
        } else {
          setBcInstallBanner("error");
        }
      })
      .catch((ex) => {
        console.error("BC marketplace claim error", ex);
        setBcInstallBanner("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasActiveConfig = configs.some((c) => c.enabled);

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "config"}
        onClick={() => setActiveTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "bot-knowledge"}
        onClick={() => setActiveTab("bot-knowledge")}
      >
        Bot Knowledge
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "skills"}
        onClick={() => setActiveTab("skills")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "mapping"}
        onClick={() => setActiveTab("mapping")}
      >
        Field Reference
      </IntegrationTab>
      {enableEcommerceSync && (
        <IntegrationTab
          active={activeTab === "shoppers"}
          onClick={() => setActiveTab("shoppers")}
        >
          Shoppers
        </IntegrationTab>
      )}
      <IntegrationTab
        active={activeTab === "log"}
        onClick={() => setActiveTab("log")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="BigCommerce" tabs={tabsJSX}>
      {bcInstallBanner === "claiming" && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Completing your BigCommerce Marketplace installation…
        </div>
      )}
      {bcInstallBanner === "success" && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 flex items-center justify-between">
          <span>BigCommerce store connected via App Marketplace.</span>
          <button
            type="button"
            className="ml-4 text-green-600 hover:text-green-800 font-semibold text-xs"
            onClick={() => setBcInstallBanner(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      {bcInstallBanner === "error" && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>
            BigCommerce Marketplace install could not be completed. Please
            connect your store manually below.
          </span>
          <button
            type="button"
            className="ml-4 text-red-600 hover:text-red-800 font-semibold text-xs"
            onClick={() => setBcInstallBanner(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {showWizard && (
        <StarterWorkflowWizard
          integration="bigcommerce"
          bigcommerceConfigId={configs.find((c) => c.enabled)?.id}
          websiteUrl={configs.find((c) => c.enabled)?.storeUrl}
          onClose={() => setShowWizard(false)}
        />
      )}

      {activeTab === "overview" && (
        <div>
          <HowItWorksCard />
          <CapabilitiesPanel />
        </div>
      )}

      {activeTab === "config" && (
        <div>
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mb-6">
                {configs
                  .filter((c) => c.enabled && !(c.isActive ?? true))
                  .map((c) => (
                    <div
                      key={c.id}
                      className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 flex items-center gap-2"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        width="16"
                        height="16"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                        <strong>{c.displayName || c.storeName}</strong> —
                        integration paused
                        {c.disabledAt
                          ? ` on ${new Date(c.disabledAt).toLocaleDateString()}`
                          : ""}
                        {c.disabledBy ? ` by ${c.disabledBy}` : ""} — bot will
                        not use this store's data.
                      </span>
                    </div>
                  ))}
                <BigCommerceAccounts
                  configs={configs}
                  onDelete={(id) =>
                    setConfigs((prev) => prev.filter((c) => c.id !== id))
                  }
                  onRefresh={fetchConfigs}
                />
              </div>

              {hasActiveConfig && (
                <DeployWidgetCallout
                  widgetDeployed={
                    configs.find((c) => c.enabled)?.widgetDeployed ?? false
                  }
                />
              )}

              {hasActiveConfig && <BigCommerceTestConnection />}
            </>
          )}
        </div>
      )}

      {activeTab === "bot-knowledge" && (
        <BigCommerceBotKnowledge
          storeHash={configs.find((c) => c.enabled)?.storeHash}
          storeUrl={configs.find((c) => c.enabled)?.storeUrl}
        />
      )}

      {activeTab === "skills" && configs.length > 0 && (
        <SectionCard className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-semibold text-zinc-800 text-lg flex items-center gap-2">
                AI Tools
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasActiveConfig ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                >
                  {hasActiveConfig ? "Active" : "No active connection"}
                </span>
              </div>
              <div className="text-sm text-zinc-500 mt-0.5">
                29 tools available. The AI calls these automatically based on
                what visitors ask — no configuration required. Connect a store
                below and they're live.
              </div>
            </div>
            {hasActiveConfig && (
              <button
                type="button"
                onClick={() => setShowWizard(true)}
                className="text-sm px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 whitespace-nowrap font-medium shadow-sm"
              >
                Launch Starter Wizard
              </button>
            )}
          </div>

          <div className="mt-8">
            <SkillControlsPanel integration="bigcommerce" skills={AI_TOOLS} />
          </div>
        </SectionCard>
      )}

      {activeTab === "mapping" && (
        <SectionCard title="Data Mapping">
          <IntegrationDataMapping
            selectedConfigId={configs.find((c) => c.enabled)?.id ?? 0}
            teamId={0}
            description="Configure which BigCommerce fields appear in the agent CRM sidebar and as workflow variables."
            guide={<BigCommerceFieldReferenceGuide />}
            guideLabel="Field Reference Guide ▾"
            fieldKey="bigCommerceField"
            fieldLabel="BigCommerce Field"
            apiPrefix="BigCommerce/Integrations"
            getFieldsPath="GetBigCommerceFields"
            scopeOptions={BC_SCOPE_OPTIONS}
          />
        </SectionCard>
      )}

      {activeTab === "shoppers" && enableEcommerceSync && (
        <SectionCard
          title="Shopper Profiles — BigCommerce"
          description="All customers synced from your connected BigCommerce store. Data updates hourly."
        >
          <ShopperProfileTab platform="BigCommerce" />
        </SectionCard>
      )}

      {activeTab === "log" && (
        <SectionCard title="Activity Log">
          <IntegrationActivityLog integrationSlug="BigCommerce" />
        </SectionCard>
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingBigCommerce() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
